import { isValidIsoDay } from './dateCore';

// URL-state encoding helpers. The URL is the source of truth for shareable
// filter/sort/pagination state; this module centralises how that state is
// serialised into `?params` and decoded back.
//
// Conventions:
//   - Sets are comma-joined (?types=Email,Report).
//   - Booleans are encoded as "1" when true; absence means false.
//   - Defaults are omitted from the URL entirely so shared links stay short.
//   - Invalid values fall back to defaults (never throw).
//   - "Set" filters where the natural default is "all selected" use a
//     screen-specific all-value list: presence of the full set omits the
//     param; absence of a param reads back as the full set.

// ── Primitive codecs ──────────────────────────────────────────────────

export function readString(
  params: URLSearchParams,
  key: string,
  defaultValue = '',
): string {
  return params.get(key) ?? defaultValue;
}

export function writeString(
  params: URLSearchParams,
  key: string,
  value: string,
  defaultValue = '',
): void {
  if (value === defaultValue || value === '') params.delete(key);
  else params.set(key, value);
}

export function readBool(
  params: URLSearchParams,
  key: string,
  defaultValue = false,
): boolean {
  const raw = params.get(key);
  if (raw === null) return defaultValue;
  return raw === '1' || raw === 'true';
}

export function writeBool(
  params: URLSearchParams,
  key: string,
  value: boolean,
  defaultValue = false,
): void {
  if (value === defaultValue) params.delete(key);
  else params.set(key, '1');
}

export function readInt(
  params: URLSearchParams,
  key: string,
  defaultValue: number,
): number {
  const raw = params.get(key);
  if (raw === null) return defaultValue;
  const n = parseInt(raw, 10);
  return Number.isFinite(n) ? n : defaultValue;
}

export function writeInt(
  params: URLSearchParams,
  key: string,
  value: number,
  defaultValue: number,
): void {
  if (value === defaultValue) params.delete(key);
  else params.set(key, String(value));
}

export function readEnum<T extends string>(
  params: URLSearchParams,
  key: string,
  allowed: readonly T[],
  defaultValue: T,
): T {
  const raw = params.get(key);
  if (raw === null) return defaultValue;
  return (allowed as readonly string[]).includes(raw) ? (raw as T) : defaultValue;
}

export function writeEnum<T extends string>(
  params: URLSearchParams,
  key: string,
  value: T,
  defaultValue: T,
): void {
  if (value === defaultValue) params.delete(key);
  else params.set(key, value);
}

// Read a comma-joined list, optionally constrained to an allow-list.
export function readStringList(
  params: URLSearchParams,
  key: string,
  defaultValue: string[] = [],
  allowed?: readonly string[],
): string[] {
  const raw = params.get(key);
  if (raw === null) return defaultValue;
  const parts = raw.split(',').map(decodeURIComponent).filter(Boolean);
  if (allowed) {
    const allow = new Set(allowed);
    return parts.filter((p) => allow.has(p));
  }
  return parts;
}

export function writeStringList(
  params: URLSearchParams,
  key: string,
  value: readonly string[],
  defaultValue: readonly string[] = [],
): void {
  // Treat "same set as default" (order-independent) as default — omit.
  if (value.length === 0 && defaultValue.length === 0) {
    params.delete(key);
    return;
  }
  const v = [...value].sort();
  const d = [...defaultValue].sort();
  if (v.length === d.length && v.every((x, i) => x === d[i])) {
    params.delete(key);
    return;
  }
  params.set(key, value.map(encodeURIComponent).join(','));
}

// Set variants — thin adapters over the list codecs.
export function readStringSet(
  params: URLSearchParams,
  key: string,
  defaultValue: Set<string> = new Set(),
  allowed?: readonly string[],
): Set<string> {
  if (params.get(key) === null) return new Set(defaultValue);
  return new Set(readStringList(params, key, [], allowed));
}

export function writeStringSet(
  params: URLSearchParams,
  key: string,
  value: Set<string>,
  defaultValue: Set<string> = new Set(),
): void {
  writeStringList(params, key, Array.from(value), Array.from(defaultValue));
}

// ── Sort ────────────────────────────────────────────────────────────────
// Encoded as "column:direction" (e.g. "date:desc"). Absence = unsorted/default.

export interface SortState<Col extends string = string> {
  column: Col | null;
  direction: 'asc' | 'desc';
}

export function readSort<Col extends string>(
  params: URLSearchParams,
  key: string,
  allowedColumns: readonly Col[],
  defaultState: SortState<Col> = { column: null, direction: 'asc' },
): SortState<Col> {
  const raw = params.get(key);
  if (raw === null) return { ...defaultState };
  const [colRaw, dirRaw] = raw.split(':');
  const column = (allowedColumns as readonly string[]).includes(colRaw)
    ? (colRaw as Col)
    : defaultState.column;
  const direction: 'asc' | 'desc' = dirRaw === 'desc' ? 'desc' : 'asc';
  return { column, direction };
}

export function writeSort<Col extends string>(
  params: URLSearchParams,
  key: string,
  value: SortState<Col>,
  defaultState: SortState<Col> = { column: null, direction: 'asc' },
): void {
  if (
    value.column === defaultState.column &&
    value.direction === defaultState.direction
  ) {
    params.delete(key);
    return;
  }
  if (value.column === null) {
    params.delete(key);
    return;
  }
  params.set(key, `${value.column}:${value.direction}`);
}

// ── Pagination ──────────────────────────────────────────────────────────
// 1-based for humans. Defaults (page=1, pageSize=25) are omitted.

export interface PaginationState {
  page: number;
  pageSize: number;
}

export const DEFAULT_PAGINATION: PaginationState = { page: 1, pageSize: 25 };
export const ALLOWED_PAGE_SIZES = [25, 50, 100] as const;
export type PageSize = (typeof ALLOWED_PAGE_SIZES)[number];

export function readPagination(
  params: URLSearchParams,
  defaultState: PaginationState = DEFAULT_PAGINATION,
): PaginationState {
  const page = Math.max(1, readInt(params, 'page', defaultState.page));
  const rawSize = readInt(params, 'pageSize', defaultState.pageSize);
  const pageSize = (ALLOWED_PAGE_SIZES as readonly number[]).includes(rawSize)
    ? rawSize
    : defaultState.pageSize;
  return { page, pageSize };
}

export function writePagination(
  params: URLSearchParams,
  value: PaginationState,
  defaultState: PaginationState = DEFAULT_PAGINATION,
): void {
  writeInt(params, 'page', value.page, defaultState.page);
  writeInt(params, 'pageSize', value.pageSize, defaultState.pageSize);
}

// ── Dates ───────────────────────────────────────────────────────────────
// ISO yyyy-mm-dd strings. Anything else is dropped.

export function readIsoDate(params: URLSearchParams, key: string): string {
  const raw = params.get(key);
  if (!raw || !isValidIsoDay(raw)) return '';
  return raw;
}

export function writeIsoDate(
  params: URLSearchParams,
  key: string,
  value: string,
): void {
  if (!value || !isValidIsoDay(value)) {
    params.delete(key);
    return;
  }
  params.set(key, value);
}

// ── Null-or-string ──────────────────────────────────────────────────────
// For filters that are either "unset" or a single string value.

export function readNullableString(
  params: URLSearchParams,
  key: string,
): string | null {
  const raw = params.get(key);
  return raw ?? null;
}

export function writeNullableString(
  params: URLSearchParams,
  key: string,
  value: string | null,
): void {
  if (value === null || value === '') {
    params.delete(key);
    return;
  }
  params.set(key, value);
}

// ── Helpers for building new URL params from an existing set ──────────

// Copy an URLSearchParams so we can mutate safely without touching the
// react-router one directly.
export function cloneParams(params: URLSearchParams): URLSearchParams {
  return new URLSearchParams(params);
}
